import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pageDataPath, pagePath, PAGE_SCHEMAS } from '../siteConfig';
import useSeoDocumentTitle from '../useSeoDocumentTitle';
import GapChip from '../components/GapChip';
import { SeoFooter } from '../components/Panels';

async function fetchSuburbIndex() {
  const response = await fetch(pageDataPath('suburb-index', 'index'), { headers: { Accept: 'application/json' } });
  const type = response.headers.get('content-type') || '';
  if (!response.ok || !type.includes('json')) return null;
  const data = await response.json();
  return data?.schema === PAGE_SCHEMAS['suburb-index'] ? data : null;
}

const norm = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * A real 404 for unknown suburb and agent URLs (served with HTTP 404 by the
 * host). Offers a suburb search, loaded only once someone types.
 */
export default function SeoNotFoundPage() {
  useSeoDocumentTitle('not-found', null);
  const [query, setQuery] = useState('');
  const wantsIndex = query.trim().length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: ['seo-suburb-index'],
    queryFn: fetchSuburbIndex,
    enabled: wantsIndex && typeof window !== 'undefined',
    staleTime: 10 * 60 * 1000,
  });

  const matches = useMemo(() => {
    if (!wantsIndex || !data) return [];
    const q = norm(query);
    return data.lgas
      .flatMap((g) => g.suburbs)
      .filter((s) => norm(s.name).includes(q) || (s.postcode || '').startsWith(q))
      .slice(0, 8);
  }, [data, query, wantsIndex]);

  return (
    <main className="re-seo re-seo--notfound" id="main">
      <div className="re-seo__inner">
        <header className="re-masthead">
          <p className="re-kicker">Page not found</p>
          <h1>We don&rsquo;t have a page at that address</h1>
          <p className="re-lede">
            The suburb or agent may be spelled differently, or may not have enough sales with a published guide yet.
          </p>
        </header>

        <div className="re-notfound-search">
          <label className="re-search">
            <span className="re-sr-only">Find a suburb</span>
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <circle cx="8.5" cy="8.5" r="5.75" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M13 13 L17.5 17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a suburb or postcode"
              autoComplete="off"
            />
          </label>
          {wantsIndex ? (
            <ul className="re-notfound-results" aria-live="polite">
              {matches.map((s) => (
                <li key={s.slug}>
                  <Link to={pagePath('suburb', s.slug)}>
                    <span>
                      {s.name} <span className="re-muted">{s.postcode}</span>
                    </span>
                    <GapChip value={s.guide_gap_pct} />
                  </Link>
                </li>
              ))}
              {!matches.length && !isFetching ? <li className="re-muted">No matching suburbs.</li> : null}
            </ul>
          ) : null}
        </div>

        <p className="re-notfound-links">
          <Link to="/suburbs">Browse all suburbs</Link>
          <Link to="/leaderboard">Agent leaderboard</Link>
          <Link to="/explore">Explore the map</Link>
        </p>

        <SeoFooter />
      </div>
    </main>
  );
}
